
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export const useDeleteAuthor = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (authorId: string): Promise<void> => {
      console.log('Starting author deletion for ID:', authorId);
      
      // Get session first, outside of try-catch for broader scope
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session for admin operations');
      }

      try {
        // Delete using service role to bypass RLS - call edge function for complete deletion
        console.log('Calling delete-author edge function...');
        const response = await fetch('https://azfvvzbppfbjozelxxmb.supabase.co/functions/v1/delete-author', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ authorId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error from delete-author function:', errorData);
          throw new Error(errorData.error || 'Failed to delete author');
        }

        const result = await response.json();
        console.log('Author deletion completed successfully:', result);

      } catch (error) {
        console.error('Error in author deletion:', error);
        
        // Fallback: Try manual deletion with improved approach
        console.log('Attempting fallback deletion method...');
        
        // Step 1: Delete ticket messages first by getting all related tickets
        console.log('Getting support tickets for cleanup...');
        const { data: tickets, error: ticketsError } = await supabase
          .from('support_tickets')
          .select('id')
          .eq('author_id', authorId);

        if (ticketsError) {
          console.error('Error fetching support tickets:', ticketsError);
          throw ticketsError;
        }

        if (tickets && tickets.length > 0) {
          const ticketIds = tickets.map(ticket => ticket.id);
          console.log('Found tickets to clean up:', ticketIds);
          
          // Delete ticket messages by individual ticket to avoid constraint issues
          console.log('Deleting ticket messages individually...');
          for (const ticketId of ticketIds) {
            let attempts = 0;
            const maxAttempts = 3;
            
            while (attempts < maxAttempts) {
              const { error: messageError } = await supabase
                .from('ticket_messages')
                .delete()
                .eq('ticket_id', ticketId);
              
              if (!messageError) {
                console.log(`Successfully deleted messages for ticket ${ticketId}`);
                break;
              } else {
                attempts++;
                console.warn(`Attempt ${attempts} failed to delete messages for ticket ${ticketId}:`, messageError);
                if (attempts === maxAttempts) {
                  console.error(`Failed to delete messages for ticket ${ticketId} after ${maxAttempts} attempts`);
                }
              }
            }
          }
          
          // Now delete support tickets
          console.log('Deleting support tickets...');
          const { error: supportTicketsError } = await supabase
            .from('support_tickets')
            .delete()
            .eq('author_id', authorId);

          if (supportTicketsError) {
            console.error('Error deleting support tickets:', supportTicketsError);
            throw supportTicketsError;
          }
        }

        // Continue with other deletions
        console.log('Deleting other author data...');
        
        // Delete royalty reports
        const { error: royaltyReportsError } = await supabase
          .from('royalty_reports')
          .delete()
          .eq('author_id', authorId);

        if (royaltyReportsError) {
          console.error('Error deleting royalty reports:', royaltyReportsError);
          throw royaltyReportsError;
        }

        // Delete books
        const { error: booksError } = await supabase
          .from('books')
          .delete()
          .eq('author_id', authorId);

        if (booksError) {
          console.error('Error deleting books:', booksError);
          throw booksError;
        }

        // Delete payment requests
        const { error: paymentRequestsError } = await supabase
          .from('payment_requests')
          .delete()
          .eq('author_id', authorId);

        if (paymentRequestsError) {
          console.error('Error deleting payment requests:', paymentRequestsError);
          throw paymentRequestsError;
        }

        // Delete payment details
        const { error: paymentDetailsError } = await supabase
          .from('payment_details')
          .delete()
          .eq('author_id', authorId);

        if (paymentDetailsError) {
          console.error('Error deleting payment details:', paymentDetailsError);
          throw paymentDetailsError;
        }

        // Delete payment methods
        const { error: paymentMethodsError } = await supabase
          .from('payment_methods')
          .delete()
          .eq('author_id', authorId);

        if (paymentMethodsError) {
          console.error('Error deleting payment methods:', paymentMethodsError);
          throw paymentMethodsError;
        }

        // Delete the profile
        const { error: profileError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', authorId);

        if (profileError) {
          console.error('Error deleting profile:', profileError);
          throw profileError;
        }

        // Delete the user from auth.users using the Edge Function
        console.log('Deleting auth user...');
        try {
          const deleteUserResponse = await fetch('https://azfvvzbppfbjozelxxmb.supabase.co/functions/v1/delete-user', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId: authorId }),
          });

          if (!deleteUserResponse.ok) {
            const errorData = await deleteUserResponse.json();
            console.error('Error deleting auth user:', errorData);
            toast({
              title: 'Warning',
              description: 'Author data deleted but user account may still exist in authentication.',
              variant: 'destructive',
            });
          } else {
            console.log('Successfully deleted auth user');
          }
        } catch (authError) {
          console.error('Error deleting auth user:', authError);
          toast({
            title: 'Warning',
            description: 'Author data deleted but user account may still exist in authentication.',
            variant: 'destructive',
          });
        }
      }

      console.log('Author deletion completed successfully');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authors'] });
      queryClient.invalidateQueries({ queryKey: ['author'] });
      
      toast({
        title: 'Success',
        description: 'Author has been deleted successfully.',
      });
      
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      console.error('Error in delete mutation:', error);
      toast({
        title: 'Error deleting author',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    }
  });
};
